import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { TrackLocations } from "@/hooks/use-segment";
import { useCancelWorkflowExecution, type PlainWorkflowExecution } from "@/queries/automation-queries";
import { formatError } from "@/utils/errors";
import { type FC } from "react";
import { WorkflowExecutionsCard } from "@/components/automations/AutomationExecutionCard";
import { useCallbackPreventDefault } from "@/hooks/use-callback-prevent-default";

type CancelWorkflowExecutionDialogProps = {
    execution: PlainWorkflowExecution;
    open: boolean;
    onClose: () => void;
};

export const CancelWorkflowExecutionDialog: FC<CancelWorkflowExecutionDialogProps> = ({ execution, open, onClose }) => {
    const { toast } = useToast();
    const cancelWorkflowExecution = useCancelWorkflowExecution();

    const handleCancel = useCallbackPreventDefault(async () => {
        try {
            await cancelWorkflowExecution.mutateAsync(execution);
            onClose();
        } catch (error) {
            toast({
                title: "Failed to cancel workflow execution",
                description: formatError(error),
            });
        }
    }, [cancelWorkflowExecution, execution, toast, onClose]);

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent
                data-testid="cancel-workflow-execution-dialog"
                data-track-location={TrackLocations.CancelWorkflowExecutionDialog}
            >
                <DialogHeader>
                    <DialogTitle>Cancel Workflow Execution</DialogTitle>
                    <DialogDescription>
                        Are you sure you want to cancel this automation execution? Running actions will be stopped.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody>
                    <WorkflowExecutionsCard execution={execution} readonly />
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-testid="cancel-workflow-execution-dialog-cancel">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructive"
                        onClick={handleCancel}
                        loading={cancelWorkflowExecution.isPending}
                        data-testid="cancel-workflow-execution-dialog-confirm"
                        data-tracking-id="confirm-cancel-workflow-execution-cancel-workflow-execution-dialog"
                    >
                        Cancel Execution
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
